/**
 * Zentrale Konfiguration: Premium-Preis, Score-Gewichte/Schwellen, Red-Flag-Schwellen,
 * Cache-TTLs. Nichts davon soll anderswo im Code hartkodiert sein - Aenderungen hier
 * wirken sich global aus, ohne Code an anderer Stelle anzufassen.
 */

/** Kanonische Produktions-URL, fuer Sitemap/Metadata/Canonical-Links. */
export const SITE_URL = "https://erikonomie.de";

/** Premium-Preis in EUR/Monat. Aenderung hier reicht, kein Code-Update noetig. */
export const PREMIUM_PRICE_EUR = 9.99;

/** Premium-Jahrespreis in EUR (Erik 2026-07-21, Rabatt ggue. 12x Monatspreis). */
export const PREMIUM_PRICE_EUR_YEARLY = 99;

/** Dritte, teurere Preis-Stufe (Erik 2026-07-22 entschieden, Namensrichtung "Basis/Pro/
 * Max" 2026-07-23 final) - reine Darstellungskonstanten wie PREMIUM_PRICE_EUR. Die
 * backend-seitige Tier-Differenzierung (lib/access.ts, Migration 0030) ist seit
 * 2026-07-28 gebaut - siehe `Tier`-Typ in lib/access.ts. */
export const MAX_PRICE_EUR = 14.99;
export const MAX_PRICE_EUR_YEARLY = 159;

/** Stripe-Zahlungsfluss bleibt deaktiviert, bis rechtliche Freigabe vorliegt. */
export const PAYMENTS_ENABLED = false;

/** Feste, von Erik jederzeit erweiterbare Vorschau-Auswahl (backlog.md ->
 * "Monetarisierung", Aktienfinder-Vorbild): bleibt vollstaendig kostenlos einsehbar,
 * sobald PAYMENTS_ENABLED aktiv ist und andere Aktien angeteasert werden - bewusst
 * eine feste Liste statt eines Kontingents ("erste X Aktien"), von Erik explizit
 * abgelehnt. Solange PAYMENTS_ENABLED false ist, hat diese Liste keine Wirkung. */
export const FREE_SHOWCASE_TICKERS: { symbol: string; name: string }[] = [
  { symbol: "MSFT", name: "Microsoft" },
  { symbol: "AAPL", name: "Apple" },
  { symbol: "PEP", name: "PepsiCo" },
  { symbol: "SAP.DE", name: "SAP" },
];

/** "Sneak Peek"-Prinzip bei Premium-Aktienanalysen (backlog.md -> [Punkt 90]):
 * kostenlose Nutzer duerfen 1-2 Analysen vollstaendig lesen, der Rest bleibt hinter
 * PremiumGate. Bewusst dieselben 2 Unternehmen wie in FREE_SHOWCASE_TICKERS
 * (Apple/Microsoft haben bereits eine Analyse in content/aktienanalysen/, siehe
 * lib/access.ts -> hasAnalysisAccess) statt einer neuen, unabhaengigen Auswahl -
 * konsistent mit dem bereits etablierten "diese Firmen sind ueberall frei"-Muster. */
export const FREE_SHOWCASE_ANALYSIS_SLUGS: string[] = ["apple", "microsoft"];

/** Social-Media-Profile fuer den Footer (backlog.md -> Feedback-Runde 19 Punkt 34) - bewusst
 * leer, bis Erik die echten Profil-URLs liefert (keine geratenen URLs eintragen). Icon kommt
 * ueber denselben Google-Favicon-Dienst wie die Datenquellen-Badges im Footer (kein eigenes
 * Marken-Icon-Set im Projekt verfuegbar, siehe components/disclaimer-footer.tsx). Reihenfolge
 * frei waehlbar, nur Plattformen eintragen, die tatsaechlich existieren. */
export const SOCIAL_LINKS: { platform: "TikTok" | "Instagram" | "YouTube" | "LinkedIn"; url: string }[] = [
  { platform: "TikTok", url: "https://www.tiktok.com/@erikonomie" },
  { platform: "Instagram", url: "https://www.instagram.com/erikonomie" },
  { platform: "YouTube", url: "https://www.youtube.com/@erikonomie" },
];

/** Feste, dauerhaft kostenlose Max-Zugaenge (backlog.md -> Feedback-Runde 19 Punkt 29:
 * Erik, Partnerin, +1) - unabhaengig von PAYMENTS_ENABLED und vom Zahlungsstatus in der
 * DB, geprueft in lib/access-server.ts VOR dem Datenbank-Lookup. Nach E-Mail-Adresse,
 * gleiche Editier-Konvention wie FREE_SHOWCASE_TICKERS. Bewusst leer ausgeliefert - keine
 * geratenen E-Mail-Adressen hartkodieren, Erik traegt die echten Adressen selbst ein. */
export const FREE_MAX_EMAILS: string[] = [
  "erikonomie@gmail.com",
  "annaleablume@gmx.de",
];

export const CACHE_TTL = {
  search: 60 * 60 * 1000,
  profile: 24 * 60 * 60 * 1000,
  historical: 24 * 60 * 60 * 1000,
  fundamentals: 6 * 60 * 60 * 1000,
  news: 15 * 60 * 1000,
  quote: 5 * 60 * 1000,
  /** Sektor-Median-Benchmark (siehe lib/data/sector-benchmark.ts) - teuer (Screener +
   * bis zu 8 Peer-Requests), aendert sich aber langsam, daher deutlich laenger als
   * `fundamentals`. Geteilt ueber ALLE Aktien desselben Sektors, nicht nur pro Symbol. */
  sectorBenchmark: 24 * 60 * 60 * 1000,
  /** Themen-Listen-Seite (siehe lib/themes/) - laut Konzept "woechentlich regeneriert",
   * bewusst deutlich laenger als sectorBenchmark statt bei jedem Seitenaufruf neu ueber
   * das gesamte Screener-Universum zu rechnen. */
  themeLists: 7 * 24 * 60 * 60 * 1000,
  /** Score-Verlauf (siehe lib/score-history/query.ts) - die zugrunde liegende
   * score_history-Tabelle aendert sich hoechstens einmal taeglich (eine Zeile pro Ticker
   * und Tag durch die Netlify Scheduled Function), eine Aktienseite wird aber oft
   * mehrfach am Tag aufgerufen - 1 Stunde spart wiederholte Supabase-Abfragen, ohne neue
   * Tagesdaten spuerbar zu verzoegern. */
  scoreHistory: 60 * 60 * 1000,
  /** Super-Investor-/13F-Tracking (Feedback-Runde 4, Punkt 13/backlog.md - KI-Copilot-
   * Paket) - SEC-Pflichtmeldungen aendern sich nur vierteljaehrlich (45-Tage-Frist nach
   * Quartalsende), geteilt ueber ALLE Aktien (jeder Fonds wird nur einmal geladen,
   * unabhaengig davon, wie viele verschiedene Symbole danach suchen). */
  superInvestor13F: 7 * 24 * 60 * 60 * 1000,
  /** Makro-News (siehe lib/macro-news/) - EZB/Destatis/Fed veroeffentlichen hoechstens
   * ein paar Mal taeglich, nicht permanent; deutlich laenger als `news` (15 Min, dort
   * pro Symbol) haelt die Uebersetzungskosten (EZB/Fed-Titel via lib/translate.ts) klein
   * und wird ueber ALLE Besucher geteilt (ein einziger, ticker-unabhaengiger Cache-Key). */
  macroNews: 30 * 60 * 1000,
} as const;

/**
 * Erikonomie-Score v3 (seit 2026-07-20, siehe docs/features/erikonomie-score.md): 10
 * gleich gewichtete Bereiche, je 0-10 Punkte, ausschliesslich aus unternehmenseigenen
 * Kennzahlen (kein Sektor-Median, kein Screener-Call). Gesamtscore = Durchschnitt der
 * Punkte ueber die VERFUEGBAREN Bereiche (fehlende Bereiche fallen aus Summe UND Teiler
 * heraus statt mit 0 gewertet zu werden), hochskaliert auf 0-100 (siehe computeScore in
 * lib/scoring/scoring.ts). Kein nachtraeglicher Red-Flag-Multiplikator mehr - jeder
 * Bereich steht fuer sich.
 *
 * Einheitliche Zeitfenster-Regel fuer alle Bereiche mit Historienbezug: so viele Jahre
 * wie verfuegbar, Ziel 10, Minimum 2 (Ausnahme Bereich 8 Wachstums-Teilscore: Minimum 5).
 * Unterschreitet die Historie das Minimum, faellt der Bereich komplett heraus. Ein
 * kuerzeres (aber gueltiges) Fenster deckelt die Punktzahl NICHT - dieselbe Formel gilt
 * unabhaengig von der tatsaechlichen Fensterlaenge.
 */
export const SCORE_CONFIG = {
  thresholds: {
    // Bereich 1: Nettomarge, einfacher Durchschnitt der letzten bis zu 10 Jahre (min. 2).
    // Reine Lineartransformation, keine Stabilitaets-/Streuungspruefung mehr.
    netMargin: { badX: 0.03, goodX: 0.3, idealYears: 10, minYears: 2 },
    // Bereich 2: ROIC, einfacher Durchschnitt der letzten bis zu 10 Jahre (min. 2) -
    // nicht mehr das schwaechste Jahr. ROIC-Definition (investiertes Kapital) unveraendert
    // aus lib/data/providers/yahoo-provider.ts uebernommen, hier nur neu aggregiert.
    roic: { badX: 0, goodX: 0.15, idealYears: 10, minYears: 2 },
    // Bereich 3: Umsatz-CAGR ueber bis zu 10 Jahre (min. 2) - cagrOverYearsWithMinimum.
    revenueCagr: { badX: 0, goodX: 0.1, idealYears: 10, minYears: 2 },
    // Bereich 4: EPS-CAGR ueber bis zu 10 Jahre (min. 2).
    epsCagr: { badX: 0, goodX: 0.15, idealYears: 10, minYears: 2 },
    // Bereich 5: Net Debt / EBITDA, aktueller Stand (keine Historie). Netto-Cash
    // (ratio <= 0) = volle Punktzahl. Kein Sektor-Adjustment mehr.
    netDebtToEbitda: { badRatio: 4.0, goodRatio: 0 },
    // Bereich 6: FCF-Marge (freeCashFlow/revenue je Jahr), Durchschnitt der letzten bis
    // zu 10 Jahre (min. 2). Kein Zusatz-Gate mehr (das war Teil des entfallenen
    // Red-Flag-Multiplikators).
    fcfMargin: { badX: 0, goodX: 0.15, idealYears: 10, minYears: 2 },
    // Bereich 7: Abweichung des aktuellen KGV vom EIGENEN historischen Durchschnitts-KGV
    // (nicht mehr Median, nicht mehr Sektor-Median) ueber bis zu 10 Jahre (min. 2
    // Jahre mit positivem KGV). +25 % teurer = 0 Pkt., -25 % billiger = 10 Pkt.
    // Bewusst eigenstaendig von OWN_PE_MEDIAN_ALERT.medianLookbackYears weiter unten
    // (score-alerts-check.mts, ScoreSummary.ownPeMedian) - dort bleibt der feste
    // 5-Jahres-MEDIAN fuer den bestehenden "KGV unter eigenem Median"-Alarm unveraendert,
    // dieser Bereich hier nutzt eine andere Fensterlaenge UND Aggregationsmethode.
    valuation: { goodDiscount: 0.25, badDiscount: -0.25, idealYears: 10, minYears: 2 },
    // Bereich 8 (zweiteilig, einzige Ausnahme von der Ein-Kennzahl-Regel): Rendite-
    // Teilscore aus der aktuellen TTM-Dividendenrendite (0 % -> 0 Pkt., >= 3 % -> 10
    // Pkt.), Wachstums-Teilscore aus der Dividenden-CAGR je Aktie ueber bis zu 10 Jahre,
    // MINDESTENS 5 Jahre (abweichend von der sonstigen 2-Jahre-Regel, da eine 2-3-Jahres-
    // Divididen-CAGR zu volatil/aussagearm waere) (0 % -> 0 Pkt., >= 10 % -> 10 Pkt.).
    // Bereich-Score = Durchschnitt beider Teilscores; ohne ausreichende Wachstumshistorie
    // nur der Rendite-Teilscore. Zahlt das Unternehmen keine Dividende (Rendite <= 0),
    // gilt der GESAMTE Bereich als nicht verfuegbar - kein Rueckkauf-/Ersatzwert.
    capitalReturn: { yieldGoodX: 0.03, dividendCagrGoodX: 0.1, idealYears: 10, minYears: 5 },
    // Bereich 9: Preis-CAGR ueber bis zu 10 Jahre (min. 2) - 0 % -> 0 Pkt., >= 12 % ->
    // 10 Pkt. Auch bei nur 2-3 Jahren Historie ist die volle Punktzahl erreichbar.
    priceCagr: { badX: 0, goodX: 0.12, idealYears: 10, minYears: 2 },
    // Bereich 10: Anteil Verlustjahre am betrachteten Fenster (bis zu 10 Jahre, min. 2).
    // "Verlustjahr" = Nettogewinn < 0 (historical.netIncome) als EBIT-Naeherung, da im
    // Projekt keine EBIT-Zeitreihe verfuegbar ist (Erik-Entscheidung 2026-07-20, siehe
    // docs/features/erikonomie-score.md fuer die Begruendung/Alternativen).
    // penaltyAcceleration (Erik-Feedback 2026-07-23, Feedback-Runde 17 Punkt 13, "der Abzug
    // ist zu flach"): der Abzug pro Verlustjahr war bisher linear/konstant (-1 Pkt./Jahr bei
    // voller Historie) - der Faktor macht den Abzug pro ZUSAETZLICHEM Verlustjahr steigend
    // statt konstant (siehe scoreLossYears() in lib/scoring/scoring.ts fuer die Formel), 0
    // waere wieder rein linear (unveraendertes altes Verhalten).
    lossYears: { idealYears: 10, minYears: 2, penaltyAcceleration: 1 },
  },
} as const;

/**
 * Schwellen fuer die Qualitaets-/Moat-Checkliste (lib/quality/checklist.ts,
 * computeRedFlags in lib/scoring/scoring.ts) - NICHT mehr Teil der Erikonomie-Score-
 * Engine selbst (der Red-Flag-Multiplikator wurde mit Score v3 am 2026-07-20 ersatzlos
 * aus dem Score entfernt), aber weiterhin die Schwellenwert-Quelle fuer vier der sechs
 * automatischen Checklisten-Kriterien - ein einziger, geteilter Schwellenwert-Pfad statt
 * mehrerer potenziell abweichender. netMarginStabilityMaxCv gehoerte frueher zu
 * SCORE_CONFIG.thresholds.netMargin (Score v3 prueft Stabilitaet/Streuung nicht mehr),
 * wird hier fuer das "stable_net_margin"-Kriterium unveraendert weitergefuehrt.
 */
export const QUALITY_CHECKLIST_THRESHOLDS = {
  netMarginStabilityMaxCv: 0.2,
  netDebtToEbitdaMax: 5.0,
  fcfCumulativeLookbackYears: 3, // kumulierter FCF ueber die letzten 3 Jahre < 0
  shareDilutionLookbackYears: 5,
  shareDilutionMaxIncrease: 0.2, // Aktienanzahl-Zuwachs > 20 % ueber 5 Jahre
} as const;

/**
 * Eigener historischer KGV-Median (siehe lib/data/score-summary.ts -> ownPeMedian,
 * netlify/functions/score-alerts-check.mts -> "KGV unter eigenem Median"-Alarm) - NICHT
 * mehr Teil der Erikonomie-Score-Engine (die nutzt seit v3 einen eigenen, laenger
 * degradierenden Durchschnitts-KGV-Vergleich, siehe SCORE_CONFIG.thresholds.valuation),
 * aber weiterhin die Fensterlaenge fuer diese zwei anderen, von der Score-Berechnung
 * unabhaengigen Features - bewusst unveraendert bei 5 Jahren/Median belassen, damit sich
 * deren Verhalten durch den Score-Umbau nicht stillschweigend mitaendert.
 */
export const OWN_PE_MEDIAN_ALERT = {
  medianLookbackYears: 5,
} as const;

/**
 * Schwellen fuer die Klumpenrisiko-Erkennung im Depot (siehe lib/portfolio/calculations.ts,
 * computeConcentrationRisk). Rein deskriptiv, keine Handlungsempfehlung.
 */
export const PORTFOLIO_CONCENTRATION = {
  /** Einzelne Position > 10 % des Depotwerts (je Waehrung). */
  positionMaxWeight: 0.1,
  /** Ein Sektor > 20 % des Depotwerts (je Waehrung). */
  sectorMaxWeight: 0.2,
} as const;

/**
 * Gewichtung der drei Streuungs-Dimensionen im Diversifikations-Score (0-100, siehe
 * lib/portfolio/diversification.ts) - Sektor zaehlt am staerksten (uebliche Haupt-
 * Streuungsachse bei Aktiendepots), Land und Einzelpositionen je etwas weniger. Summe
 * ergibt 1, damit der Gesamtscore weiterhin im 0-100-Bereich bleibt.
 */
export const PORTFOLIO_DIVERSIFICATION = {
  sectorWeight: 0.4,
  countryWeight: 0.3,
  companyWeight: 0.3,
} as const;

/**
 * Kaufpreis-Plausibilisierung (siehe lib/portfolio/purchase-price-check.ts,
 * backlog.md -> Punkt 3, letzter Punkt): vergleicht einen eingegebenen Kaufpreis mit dem
 * tatsaechlichen Schlusskurs am Kaufdatum. Bewusst grosszuegige Schwelle statt einer
 * Tick-genauen Pruefung - ein legitimer Intraday-Kauf kann durchaus deutlich vom
 * Schlusskurs desselben Tages abweichen, das Ziel ist nur, grobe Tippfehler/falsche
 * Jahresangaben zu erkennen, keine exakte Kursvalidierung.
 */
export const PURCHASE_PRICE_CHECK = {
  implausibleDeviationPct: 0.15,
} as const;

/**
 * Depot-Score-Alarm (siehe backlog.md -> "Depot-Score-Alarm",
 * lib/portfolio/score-alert.ts). Drawdown-basiert gegenueber dem letzten
 * Hoechststand statt einer festen Schwelle - siehe dort fuer die Begruendung.
 */
export const PORTFOLIO_SCORE_ALERT = {
  /** Ab wie vielen Punkten Rueckgang gegenueber dem letzten Hoechststand als
   * "spuerbar" gilt und eine Benachrichtigung ausloest. */
  minDropPoints: 5,
} as const;

/**
 * Institutionelle Peer-Group-Engine (siehe lib/peer-group/). Nutzer fuegen
 * Vergleichsaktien weiterhin manuell hinzu (components/peer-compare.tsx) - diese
 * Schwellen filtern/kappen die daraus gebildete quantitative Peer-Gruppe.
 */
export const PEER_GROUP = {
  /** Kandidat darf max. 75 % kleiner sein als die Ziel-Aktie. */
  minMarketCapRatio: 0.25,
  /** und max. 1000 % (11x) groesser. */
  maxMarketCapRatio: 11,
  /** "Top 8" laut Spec, um visuelles Overcrowding zu vermeiden. */
  maxPeers: 8,
  /** Zusaetzlich zur kontinuierlichen Ratio-Klammer oben: absolute Groessenklassen-Grenzen
   * (backlog.md -> "Peer-Group zusaetzlich nach Marktkapitalisierungsklasse verfeinern,
   * damit Large-Cap nicht mit Small-Cap verglichen wird") - grob an gaengigen
   * Marktkapitalisierungs-Konventionen orientiert, keine feste regulatorische Definition.
   * Verhindert Faelle, in denen zwei Aktien noch innerhalb der 0.25x-11x-Ratio-Klammer
   * liegen, aber in absoluten Groessenklassen auseinanderfallen (z.B. 3 Mrd. vs. 30 Mrd. -
   * Ratio 10x waere noch erlaubt, Small/Mid- vs. Large-Cap aber nicht). Werte in der
   * jeweiligen Notierungswaehrung der Aktie, gleiche Vereinfachung wie bei
   * minMarketCapRatio/maxMarketCapRatio oben (keine Waehrungsumrechnung). */
  marketCapClassThresholds: {
    /** >= 10 Mrd. = "large". */
    large: 10_000_000_000,
    /** >= 2 Mrd., < 10 Mrd. = "mid"; darunter "small". */
    mid: 2_000_000_000,
  },
} as const;

/**
 * Peer-Divergenz-Alarm (siehe netlify/functions/score-alerts-check.mts, backlog.md ->
 * Punkt 18): loest aus, wenn das eigene KGV um mindestens diesen relativen Anteil vom
 * KGV des am besten passenden Peers (rankAutoSuggestedPeers, Tier-1-exakte-Industrie
 * bevorzugt) abweicht - in beide Richtungen (guenstiger UND teurer als der Peer).
 */
export const PEER_DIVERGENCE_ALERT = {
  minAbsoluteDivergence: 0.3,
} as const;

/**
 * Score-Trend-Alarm je Symbol (siehe backlog.md -> "Weiterer Nutzwert-Katalog" ->
 * Score/Analyse, "zusätzlich zum bestehenden Schwellenwert-Alert"; lib/notifications/
 * symbol-alerts.ts). Gleiches Drawdown-Prinzip wie PORTFOLIO_SCORE_ALERT oben, hier auf
 * ein einzelnes Symbol angewendet - etwas grosszuegiger bemessen (10 statt 5 Punkte), da
 * eine einzelne Aktie natuerlich staerker schwankt als ein diversifizierter, gewichteter
 * Depot-Durchschnitt.
 */
export const SCORE_TREND_ALERT = {
  minDropPoints: 10,
} as const;

/**
 * Analysten-Kurszielkonsens-Aenderung als eigener Alarm (siehe backlog.md -> "Weiterer
 * Nutzwert-Katalog" -> Score/Analyse, "nicht nur ein fixer Schwellenwert"). Vergleicht
 * den aktuellen mittleren Kursziel-Konsens gegen den zuletzt benachrichtigten Referenzwert
 * (score_alerts.consensus_change_reference) - loest in BEIDE Richtungen aus (Analysten
 * heben ODER senken den Konsens deutlich an), gleiches Zwei-Richtungs-Prinzip wie
 * PEER_DIVERGENCE_ALERT. 10 % statt dessen 30 %, da Kurszielrevisionen typischerweise
 * kleinschrittiger ausfallen als KGV-Abweichungen zwischen zwei Unternehmen.
 */
export const CONSENSUS_CHANGE_ALERT = {
  minAbsoluteChange: 0.1,
} as const;

/**
 * Gewichtungs-Presets fuer den "Target-Fit"-Toggle der Peer-Group-Engine. Jedes Preset
 * summiert sich auf 1.0. Metrik-Keys muessen mit den in lib/peer-group/radar.ts
 * abgeleiteten Kennzahlen uebereinstimmen.
 */
export const TARGET_FIT_PRESETS = {
  cashflow: {
    label: "Fokus Cashflow",
    weights: {
      dividendYield: 0.3,
      fcfYield: 0.3,
      netMargin: 0.15,
      roic: 0.15,
      revenueCagr3y: 0.05,
      debtToEquity: 0.05,
    },
  },
  growth: {
    label: "Fokus Wachstum",
    weights: {
      revenueCagr3y: 0.35,
      epsCagr3y: 0.35,
      roic: 0.1,
      netMargin: 0.1,
      dividendYield: 0.05,
      debtToEquity: 0.05,
    },
  },
  quality: {
    label: "Fokus Burggraben/Qualität",
    weights: {
      roic: 0.3,
      netMargin: 0.25,
      roe: 0.2,
      debtToEquity: 0.15,
      revenueCagr3y: 0.1,
    },
  },
} as const;

/**
 * Fairer-Wert-Verlaufschart (siehe lib/valuation/fair-value.ts, backlog.md -> Punkt 1).
 * EPS x durchschnittliches KGV der eigenen Historie - bewusst kein fixer
 * Branchenmultiplikator wie FASTgraphs' "15x", das waere eine erfundene Konstante.
 */
export const FAIR_VALUE = {
  /** Unter dieser Anzahl an Jahren mit positivem (= definiertem) KGV ist ein
   * Durchschnitts-KGV statistisch nicht aussagekraeftig genug fuer eine Fair-Value-Linie -
   * dann lieber ein ehrlicher Hinweistext als eine bedeutungslose Linie. */
  minPeYears: 5,
  /** Letzter Fallback (nur FairValueSimulator, siehe backlog.md -> Runde 8 Punkt 10),
   * ausschliesslich wenn selbst die VOLLE Historie minPeYears nicht erreicht - betrifft
   * strukturell fast nur Aktien ohne SEC-Registrierung/US-ADR (Yahoo liefert dort oft nur
   * ~4-5 statt SEC-EDGARs 10+ Jahre, siehe STATUS.md -> "Historische Datentiefe"). Ohne
   * diesen zweiten, niedrigeren Boden war das komplette Werkzeug (Chart, Multiples,
   * Zeitraum-Vergleich, Aktienkauf-Rechner) fuer diese Aktien dauerhaft durch einen
   * einzeiligen Hinweistext ersetzt, nie nur der Zeitraum-Filter wie beim bereits
   * bestehenden usedFallbackRange-Fall. 2 (statt z.B. 1) bleibt bewusst noch ein echter
   * Durchschnitt, kein Einzeljahr-Wert - sichtbar als Hinweis auf die kleinere
   * Datengrundlage ausgewiesen, kein stilles Umschalten. */
  minPeYearsReduced: 2,
  /** Schwelle fuer die volle EPS-basierte Variante des Fairer-Wert-Alarms (backlog.md ->
   * Punkt 7, ScoreSummary.fairValueDiscount) - loest erst aus, wenn der aktuelle Kurs
   * mindestens so weit unter der vollen Fair-Value-Linie liegt (nicht nur unter dem
   * Median wie bei der bereits bestehenden einfachen Alert-Variante). Erbt automatisch
   * die Ehrlichkeits-Sperre von computeFairValueSeries (minPeYears) - fairValueDiscount
   * ist undefined, wenn nicht genug vertrauenswuerdige Jahre vorliegen. */
  alertDiscountThreshold: 0.15,
} as const;

/**
 * Reverse-DCF (siehe lib/valuation/reverse-dcf.ts, backlog.md -> D6): rechnet aus dem
 * aktuellen Kurs zurueck, welches konstante FCF-Wachstum ueber `projectionYears` Jahre
 * noetig waere, um genau die heutige Marktkapitalisierung zu rechtfertigen (Discounted-
 * Cashflow-Modell rueckwaerts geloest statt vorwaerts). Zwei der drei Konstanten sind
 * bewusst grobe, langfristige Schaetzwerte statt live abgerufener Praezisionszahlen -
 * eine exakte, taegliche Eigenkapitalrisikopraemie (z.B. Damodaran-Style) ist ueber
 * keine im Projekt genutzte kostenlose Quelle verfuegbar, ein erfundener Praezisionswert
 * waere schlimmer als ein grober, dokumentierter Schaetzwert.
 */
export const REVERSE_DCF = {
  /** Eigenkapitalrisikopraemie (Aufschlag auf den risikofreien Zins fuer Aktien
   * gegenueber Staatsanleihen) - grober, langfristiger Schaetzwert (historische
   * US-Marktrisikopraemien liegen ueblicherweise im Bereich 4-5,5 %), kein taggenauer
   * Live-Wert. Diskontsatz = FRED 10J-US-Staatsanleihenrendite + diese Praemie. */
  equityRiskPremium: 0.045,
  /** Ewige Wachstumsrate nach dem Prognosezeitraum - grob am langfristig erwarteten
   * nominalen BIP-Wachstum orientiert, MUSS kleiner als der Diskontsatz sein (sonst
   * divergiert die Gordon-Growth-Formel). */
  terminalGrowthRate: 0.025,
  /** Laenge des expliziten Prognosezeitraums, gleiche "10 Jahre" Konvention wie an
   * anderer Stelle im Projekt (10J-Kurs-CAGR-KPI, Dividenden-Einkommens-Hochrechnung). */
  projectionYears: 10,
} as const;

/**
 * Woechentlicher Digest, "grosse Bewegungen"-Ergaenzung (siehe
 * lib/notifications/weekly-digest.ts, backlog.md -> C20/"F12").
 */
export const WEEKLY_DIGEST = {
  /** Fenster fuer den Kursvergleich - passt zum woechentlichen Versandrhythmus. */
  priceMoveWindowDays: 7,
  /** Ab dieser relativen Kursaenderung im Fenster gilt eine Bewegung als "auffaellig"
   * genug, um im Digest erwaehnt zu werden (sonst wuerde jede Aktie jede Woche eine
   * Zeile bekommen, auch bei normalem Rauschen). */
  notableMoveThreshold: 0.08,
} as const;

/**
 * Deutsche Vorabpauschale fuer thesaurierende Fonds/ETFs (siehe lib/tax/vorabpauschale.ts,
 * backlog.md -> Punkt 12). Basiszins ist eine jaehrlich vom Bundesfinanzministerium per
 * BMF-Schreiben veroeffentlichte Zahl (§ 18 Abs. 4 InvStG) - keine live-abrufbare
 * Datenquelle im Projekt-Stil, daher als reiner Konfigurationswert gepflegt (wie
 * PREMIUM_PRICE_EUR), MUSS jeweils im Januar auf den neu veroeffentlichten Wert
 * aktualisiert werden.
 */
export const VORABPAUSCHALE = {
  /** Basiszins zum 2. Januar 2026 = 3,20 % - BMF-Schreiben vom 13.01.2026 (GZ IV C 1 -
   * S 1980/00230/012/001, "Basiszins zur Berechnung der Vorabpauschale gemäß § 18
   * Absatz 4 InvStG"), gegen das Original-PDF verifiziert (nicht aus Trainingsdaten
   * geraten). Gilt fuer das Steuerjahr 2026. */
  basiszins: 0.032,
  /** Teilfreistellungssaetze nach § 20 InvStG (Privatanleger, stabile Gesetzeswerte,
   * aendern sich anders als der Basiszins nicht jaehrlich). */
  partialExemptionRates: {
    equity: 0.3,
    mixed: 0.15,
    realEstate: 0.6,
    foreignRealEstate: 0.8,
    other: 0,
  },
  /** Abgeltungsteuer 25 % + Solidaritaetszuschlag 5,5 % darauf = 26,375 % effektiv, fuer
   * die grobe Steuerschaetzung in der UI. Bewusst OHNE Kirchensteuer (individuell/
   * optional) und OHNE Sparerpauschbetrag (personen-/depotuebergreifend, hier nicht
   * bekannt) - beides in der UI als Einschraenkung benannt statt stillschweigend
   * anzunehmen. */
  flatTaxRate: 0.26375,
} as const;

/**
 * Deutsche Bankeinlagen-Durchschnittszinsen als Einordnungshilfe fuer die
 * Dividendenrendite (backlog.md -> "Dividendenrendite im Vergleich zu Sparbuch/
 * Festgeld-Durchschnittszins"). Wie VORABPAUSCHALE.basiszins keine live-abrufbare
 * Datenquelle im Projekt-Stil (FRED fuehrt keine deutschen Tagesgeld-/Festgeld-
 * Vergleichszinsen), daher als reiner, mit Quelle+Stand belegter Konfigurationswert
 * gepflegt - MUSS von Zeit zu Zeit aktualisiert werden, wenn sich das Zinsniveau
 * spuerbar verschiebt (kein taeglich pruefbarer Wert wie ein Aktienkurs).
 */
export const SAVINGS_REFERENCE_RATES = {
  /** Tagesgeld-Durchschnitt aller verglichenen Banken zum 01.08.2026 = 1,95 % p.a.
   * Quelle: tagesgeldvergleich.net Marktanalyse
   * (https://www.tagesgeldvergleich.net/veroeffentlichungen/marktanalyse.html), live
   * abgerufen und gegengeprueft (nicht aus Trainingsdaten geraten). */
  tagesgeld: 0.0195,
  /** Festgeld-Durchschnitt (12 Monate Laufzeit) aller verglichenen Banken zum
   * 01.08.2026 = 2,35 % p.a. Gleiche Quelle/Stand wie tagesgeld oben. */
  festgeld12m: 0.0235,
  /** Stand der beiden Werte oben, fuer den UI-Hinweistext. */
  asOf: "2026-08-01",
} as const;

/** Kuratierte Waehrungsliste fuer manuelle Waehrungs-Dropdowns (Depot-Kaufwaehrung,
 * Dividenden-Tracker-Eintrag) - Waehrungen der tatsaechlich unterstuetzten Screener-/
 * Depot-Maerkte (siehe lib/data/fx-rates.ts). Eine geteilte Quelle statt an mehreren
 * Stellen dieselbe Liste zu pflegen (Runde 5, Punkt 18). */
export const CURRENCY_OPTIONS = ["EUR", "USD", "GBP", "CHF", "JPY", "CAD", "AUD", "HKD"] as const;

/**
 * Index-Proxy-Ticker fuer den Depot-vs-Index-Vergleich (siehe
 * lib/portfolio/depot-vs-index.ts, backlog.md -> Punkt 10). Bewusst gewaehlt und
 * dokumentiert statt vage "DAX"/"S&P 500" zu zeigen - Kursindex vs. thesaurierender
 * Total-Return-Proxy haben eine unterschiedliche Dividenden-Charakteristik, die in der UI
 * transparent benannt werden muss (Vorgabe aus dem Backlog-Eintrag). Alle drei Ticker
 * gegen die echte Yahoo-Chart-API verifiziert (2026-07-15), bevor sie hier hinterlegt
 * wurden.
 */
export const INDEX_PROXIES = {
  dax: {
    symbol: "^GDAXI",
    label: "DAX",
    note: "Performance-Index - Dividenden sind (anders als bei den meisten Indizes) bereits eingerechnet.",
  },
  sp500: {
    symbol: "^GSPC",
    label: "S&P 500",
    note: "Reiner Kursindex ohne Dividenden - die tatsächliche Gesamtrendite läge inkl. Ausschüttungen etwas höher.",
  },
  msciWorld: {
    symbol: "EUNL.DE",
    label: "MSCI World",
    note: "Kein direkter MSCI-World-Index verfügbar, daher ETF-Proxy (iShares Core MSCI World UCITS ETF, thesaurierend) - bildet die Gesamtrendite inkl. reinvestierter Dividenden näherungsweise ab.",
  },
  // backlog.md -> "Depot-vs-Index um ... Stoxx Europe 600 erweitern" (Erik 2026-07-29).
  // ^STOXX gegen die echte Yahoo-Chart-API verifiziert (2026-07-29): "STXE 600 I", EUR,
  // Historie seit 2004 - der direkte Preisindex-Ticker, kein ETF-Proxy noetig (anders als
  // MSCI World oben).
  stoxx600: {
    symbol: "^STOXX",
    label: "Stoxx Europe 600",
    note: "Reiner Kursindex ohne Dividenden - die tatsächliche Gesamtrendite läge inkl. Ausschüttungen etwas höher.",
  },
} as const;

/**
 * Multi-Quellen-Datenvalidierung (siehe lib/data/validation/). Kreuzvalidiert einzelne
 * Fundamentaldaten der primaeren Quelle (Yahoo/FMP) gegen eine Sekundaerquelle (Alpha
 * Vantage), um freie Datenquellen auf ein hoeheres Qualitaetsniveau zu heben.
 */
export const DATA_VALIDATION = {
  /** Abweichung zwischen Primaer- und Sekundaerquelle > 5 % -> Wert als "Ungeprueft" markieren. */
  maxDeviationPct: 0.05,
  /** Mehrtaegiges Cache-TTL, schont die knappen Alpha-Vantage-Tageskontingente
   * (25 Requests/Tag im Free-Tier, projektweit geteilt) - Validierungsergebnis muss
   * nicht bei jedem Seitenaufruf neu ermittelt werden. */
  cacheTtlMs: 7 * 24 * 60 * 60 * 1000,
} as const;

/**
 * Gemeinsames Tages-Budget fuer ALLE Alpha-Vantage-Aufrufe im Projekt (siehe
 * lib/data/alpha-vantage-budget.ts) - geteilt zwischen der Multi-Quellen-
 * Datenvalidierung (DATA_VALIDATION, 1 Request/Symbol) und der Historien-Anreicherung
 * fuer nicht-US-Aktien (ALPHA_VANTAGE_HISTORY, bis zu 4 Requests/Symbol). Verhindert,
 * dass eine der beiden Funktionen der anderen versehentlich das komplette
 * 25-Requests/Tag-Freikontingent wegnimmt (siehe backlog.md -> "Historische Datentiefe").
 */
export const ALPHA_VANTAGE_BUDGET = {
  /** PRO Alpha-Vantage-Key (siehe lib/data/alpha-vantage-keys.ts - bei mehreren
   * kostenlosen Keys skaliert lib/data/alpha-vantage-budget.ts das effektive
   * Tageskontingent automatisch mit deren Anzahl). */
  dailyLimit: 25,
  /** Anteil des effektiven Tageskontingents, den der Prewarm-Job (netlify/functions/
   * alpha-vantage-prewarm.mts) maximal fuer Watchlist-/Depot-Symbole verbrauchen darf -
   * der Rest bleibt fuer organischen Traffic reserviert, damit eine am selben Tag
   * erstmals gesuchte Aktie nicht strukturell benachteiligt wird, nur weil das
   * Vorwaermen um Mitternacht zuerst laeuft (Erik: "10 Jahre Tiefe zu jeder Zeit"). */
  prewarmMaxShare: 0.6,
} as const;

/**
 * Historien-Anreicherung fuer nicht-US-Aktien ueber Alpha Vantage (SYMBOL_SEARCH +
 * INCOME_STATEMENT + BALANCE_SHEET + CASH_FLOW), siehe lib/data/alpha-vantage-history/.
 * Ergaenzt die SEC-EDGAR-Ergaenzung (nur US-Filer) um ein Aequivalent fuer alle anderen
 * Boersen - gleiche Rolle wie SEC in yahoo-provider.ts, andere Quelle.
 */
export const ALPHA_VANTAGE_HISTORY = {
  /** 90 statt 7 Tage (DATA_VALIDATION.cacheTtlMs): Jahresabschluesse aendern sich selten,
   * ein deutlich laengeres Cache-TTL schont das gemeinsame Tageskontingent zusaetzlich. */
  cacheTtlMs: 90 * 24 * 60 * 60 * 1000,
  /** Kuerzer als cacheTtlMs oben (backlog.md -> [Punkt 78], Quartalsbericht-Fallback fuer
   * europaeische Werte ohne SEC-Pressemitteilung) - ein neues Quartal erscheint alle ~3
   * Monate, 14 Tage haelt die Anzeige spuerbar aktueller als der 90-Tage-Jahres-Cache,
   * ohne das gemeinsame Tageskontingent zu strapazieren (dieser Pfad wird ohnehin nur
   * bei tatsaechlichem Seitenaufruf ausgeloest, nicht in einem Bulk-Job). */
  cacheTtlMsQuarterly: 14 * 24 * 60 * 60 * 1000,
} as const;

/**
 * Historien-Anreicherung fuer UK-Aktien ohne eigene SEC/ADR-Registrierung ueber die FCA
 * National Storage Mechanism (echte ESEF/UKSEF-xBRL-Jahresberichte, siehe
 * lib/data/uk-nsm/). Gleiche Rolle wie ALPHA_VANTAGE_HISTORY/SEC in yahoo-provider.ts,
 * andere Quelle - kein Tageskontingent (keyless), daher kein Budget-Zaehler noetig, nur
 * dasselbe lange Cache-TTL (Jahresberichte aendern sich nicht untertaegig).
 */
export const UK_NSM_HISTORY = {
  cacheTtlMs: 90 * 24 * 60 * 60 * 1000,
} as const;
